import React, { useEffect } from "react";
import { useEditor, EditorContent, Editor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Link from "@tiptap/extension-link";
import {
  Bold,
  Italic,
  List,
  ListOrdered,
  Link as LinkIcon,
  Undo,
  Redo,
} from "lucide-react";
import { cn } from "../../../lib/utils";

interface TipTapEditorProps {
  content: string;
  onChange: (html: string) => void;
  onFocus?: () => void;
  placeholder?: string;
  minHeight?: string;
}

interface ToolbarButtonProps {
  onClick: () => void;
  active?: boolean;
  title: string;
  children: React.ReactNode;
  disabled?: boolean;
}

const ToolbarButton: React.FC<ToolbarButtonProps> = ({
  onClick,
  active,
  title,
  children,
  disabled,
}) => (
  <button
    type="button"
    onClick={onClick}
    title={title}
    disabled={disabled}
    className={cn(
      "h-8 w-8 inline-flex items-center justify-center rounded text-sm transition-colors",
      "hover:bg-muted disabled:opacity-50 disabled:cursor-not-allowed",
      active && "bg-muted text-foreground"
    )}
  >
    {children}
  </button>
);

const Toolbar: React.FC<{ editor: Editor | null }> = ({ editor }) => {
  if (!editor) return null;

  const setLink = () => {
    const previousUrl = editor.getAttributes("link").href;
    // Prompt is acceptable here for a simple URL input — same as native input semantics
    // eslint-disable-next-line no-alert
    const url = window.prompt("Adres URL", previousUrl || "https://");
    if (url === null) return;
    if (url === "") {
      editor.chain().focus().extendMarkRange("link").unsetLink().run();
      return;
    }
    editor.chain().focus().extendMarkRange("link").setLink({ href: url }).run();
  };

  return (
    <div className="flex flex-wrap items-center gap-1 border-b border-border bg-muted/30 p-1.5">
      <ToolbarButton
        onClick={() => editor.chain().focus().toggleBold().run()}
        active={editor.isActive("bold")}
        title="Pogrubienie (Ctrl+B)"
      >
        <Bold className="h-4 w-4" />
      </ToolbarButton>
      <ToolbarButton
        onClick={() => editor.chain().focus().toggleItalic().run()}
        active={editor.isActive("italic")}
        title="Kursywa (Ctrl+I)"
      >
        <Italic className="h-4 w-4" />
      </ToolbarButton>
      <div className="mx-1 h-5 w-px bg-border" />
      <ToolbarButton
        onClick={() => editor.chain().focus().toggleBulletList().run()}
        active={editor.isActive("bulletList")}
        title="Lista punktowana"
      >
        <List className="h-4 w-4" />
      </ToolbarButton>
      <ToolbarButton
        onClick={() => editor.chain().focus().toggleOrderedList().run()}
        active={editor.isActive("orderedList")}
        title="Lista numerowana"
      >
        <ListOrdered className="h-4 w-4" />
      </ToolbarButton>
      <div className="mx-1 h-5 w-px bg-border" />
      <ToolbarButton
        onClick={setLink}
        active={editor.isActive("link")}
        title="Wstaw link"
      >
        <LinkIcon className="h-4 w-4" />
      </ToolbarButton>
      <div className="mx-1 h-5 w-px bg-border" />
      <ToolbarButton
        onClick={() => editor.chain().focus().undo().run()}
        title="Cofnij (Ctrl+Z)"
        disabled={!editor.can().undo()}
      >
        <Undo className="h-4 w-4" />
      </ToolbarButton>
      <ToolbarButton
        onClick={() => editor.chain().focus().redo().run()}
        title="Przywróć (Ctrl+Shift+Z)"
        disabled={!editor.can().redo()}
      >
        <Redo className="h-4 w-4" />
      </ToolbarButton>
    </div>
  );
};

export interface TipTapEditorHandle {
  insertText: (text: string) => void;
}

export const TipTapEditor = React.forwardRef<TipTapEditorHandle, TipTapEditorProps>(
  ({ content, onChange, onFocus, minHeight = "240px" }, ref) => {
    const editor = useEditor({
      extensions: [
        StarterKit.configure({
          link: false,
        }),
        Link.configure({
          openOnClick: false,
          HTMLAttributes: {
            class: "text-primary underline",
          },
        }),
      ],
      content,
      onUpdate: ({ editor }) => {
        onChange(editor.getHTML());
      },
      onFocus: () => {
        onFocus?.();
      },
      editorProps: {
        attributes: {
          class:
            "prose prose-sm max-w-none focus:outline-none px-3 py-3 text-foreground",
        },
      },
    });

    // Sync external content changes (e.g., when switching templates)
    useEffect(() => {
      if (!editor) return;
      const current = editor.getHTML();
      if (current !== content) {
        editor.commands.setContent(content || "", { emitUpdate: false });
      }
    }, [content, editor]);

    // Expose imperative API for inserting variables at cursor
    React.useImperativeHandle(
      ref,
      () => ({
        insertText: (text: string) => {
          if (!editor) return;
          editor.chain().focus().insertContent(text).run();
        },
      }),
      [editor]
    );

    return (
      <div className="rounded-md border border-input bg-background overflow-hidden">
        <Toolbar editor={editor} />
        <div style={{ minHeight }} className="overflow-auto">
          <EditorContent editor={editor} />
        </div>
      </div>
    );
  }
);

TipTapEditor.displayName = "TipTapEditor";
